import { useEffect, useState } from 'react';
import type { JSX } from 'react';
import { Crosshair, PencilRuler } from 'lucide-react';
import type { EditorController } from '../editor/controller.js';
import { useEditor } from '../state/store.js';
import { plural } from '../copy.js';

/**
 * Plan mode: arrange the walls you have laid into a building (phase 2, M5).
 *
 * The drawing surface is where you position walls; this panel is for the
 * things a drag cannot say — exact numbers, and whether the outline actually
 * closes, which is what the roof depends on.
 *
 * It used to carry a second list of walls of its own, with its own selection
 * separate from the wall being drawn. Both are gone: the walls list in the
 * right panel is the only one, and the wall it highlights is the wall this
 * panel positions.
 */
export function PlanPanel({
  controller,
  run,
}: {
  readonly controller: EditorController | null;
  readonly run: (id: string) => void;
}): JSX.Element {
  const project = useEditor((s) => s.project);
  const setTool = useEditor((s) => s.setTool);
  useEditor((s) => s.docRevision);
  const [, setTick] = useState(0);
  const redraw = (): void => setTick((value) => value + 1);

  const activeWallId = project.activeWallId;
  useEffect(redraw, [activeWallId]);

  const building = controller?.building() ?? null;
  const footprint = building?.footprint ?? null;
  const selected = building?.walls.find((wall) => wall.id === activeWallId) ?? null;

  return (
    <>
      <section>
        <h2>Outline</h2>
        {footprint === null ? (
          <p className="readout">Nothing to arrange yet</p>
        ) : footprint.closed ? (
          <p className="readout">
            Closed · {footprint.ring.length} corners{'\n'}
            {(footprint.areaMm2 / 1_000_000).toFixed(2)} m² enclosed
          </p>
        ) : (
          <p className="readout warn">
            Not closed{'\n'}
            {footprint.openEnds.length === 0
              ? 'The walls form more than one loop'
              : `${footprint.openEnds.length} wall ${
                  footprint.openEnds.length === 1 ? 'end joins' : 'ends join'
                } nothing`}
          </p>
        )}
        <p className="note">
          {footprint?.roofSupport === 'hip-and-gable'
            ? 'A hip or a gable roof can be built on this outline.'
            : 'A hip roof needs a closed outline with right-angled or convex corners. This one would fall back to a gable over its bounding box.'}
        </p>
        {building !== null && building.overlaps.length > 0 && (
          <p className="note">
            {plural(building.overlaps.length)
              .replace('bricks', 'corners')
              .replace('brick', 'corner')}{' '}
            where two walls share the same space. Walls are placed exactly as drawn, so those
            overlaps are left as they are.
          </p>
        )}
        <div className="actions">
          <button className="btn" onClick={() => run('view.fit')} title="Fit the building (F)">
            <Crosshair size={14} /> Fit building
          </button>
        </div>
      </section>

      {selected !== null && (
        <section>
          <h2>Where {selected.name} sits</h2>
          <div className="grid2">
            <label>
              X (mm)
              <input
                type="number"
                step="1"
                value={round(selected.placement.origin.x)}
                onChange={(event) => {
                  const x = Number(event.target.value);
                  if (!Number.isFinite(x)) return;
                  controller?.setPlanPlacement({
                    ...selected.placement,
                    origin: { ...selected.placement.origin, x },
                  });
                  redraw();
                }}
              />
            </label>
            <label>
              Z (mm)
              <input
                type="number"
                step="1"
                value={round(selected.placement.origin.z)}
                onChange={(event) => {
                  const z = Number(event.target.value);
                  if (!Number.isFinite(z)) return;
                  controller?.setPlanPlacement({
                    ...selected.placement,
                    origin: { ...selected.placement.origin, z },
                  });
                  redraw();
                }}
              />
            </label>
            <label>
              Turn (°)
              <input
                type="number"
                step="15"
                value={round(selected.placement.rotationDeg)}
                onChange={(event) => {
                  const rotationDeg = Number(event.target.value);
                  if (!Number.isFinite(rotationDeg)) return;
                  controller?.setPlanPlacement({ ...selected.placement, rotationDeg });
                  redraw();
                }}
              />
            </label>
            <label>
              Thickness (mm)
              <input
                type="number"
                step="0.5"
                min="1"
                value={round(selected.placement.thicknessMm)}
                onChange={(event) => {
                  controller?.setPlanThickness(Number(event.target.value));
                  redraw();
                }}
              />
            </label>
          </div>
          <p className="note">
            The heavy edge is the outer face — the elevation you drew. Turning the wall swings it
            about the end nearest its start.
          </p>
          <div className="actions">
            <button className="btn" onClick={() => setTool('build')}>
              <PencilRuler size={14} /> Draw on this wall
            </button>
          </div>
        </section>
      )}

      <section>
        <h2>Roof</h2>
        <div className="grid2">
          <label>
            Style
            <select
              value={project.roof.style}
              onChange={(event) => {
                controller?.setRoof({ style: event.target.value as 'hip' | 'gable' | 'none' });
                redraw();
              }}
            >
              <option value="hip">Hip</option>
              <option value="gable">Gable</option>
              <option value="none">No roof</option>
            </select>
          </label>
          <label>
            Pitch (°)
            <input
              type="number"
              min="5"
              max="75"
              step="1"
              value={round(project.roof.pitchDeg)}
              onChange={(event) => {
                const pitchDeg = Number(event.target.value);
                if (!Number.isFinite(pitchDeg)) return;
                controller?.setRoof({ pitchDeg });
                redraw();
              }}
            />
          </label>
          <label>
            Overhang (mm)
            <input
              type="number"
              min="0"
              step="50"
              value={round(project.roof.overhangMm)}
              onChange={(event) => {
                const overhangMm = Number(event.target.value);
                if (!Number.isFinite(overhangMm) || overhangMm < 0) return;
                controller?.setRoof({ overhangMm });
                redraw();
              }}
            />
          </label>
          <label>
            Tiles
            <select
              value={project.roof.materialId}
              onChange={(event) => {
                controller?.setRoof({ materialId: event.target.value });
                redraw();
              }}
            >
              {project.materials.map((material) => (
                <option key={material.id} value={material.id}>
                  {material.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="note">
          {footprint?.roofSupport === 'hip-and-gable'
            ? 'Built from the outline. Change a wall and the roof follows.'
            : 'This outline falls back to a gable over its bounding box.'}
        </p>
      </section>
    </>
  );
}

/** Fields show whole millimetres; the model keeps whatever precision it has. */
function round(value: number): number {
  return Math.round(value * 1000) / 1000;
}
